import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { User, UserResponse, GraphViewPreference, GraphViewPref, UserPreferences } from '../_models/user.model';
import { catchError, distinctUntilChanged, filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { BehaviorSubject, defer, merge, Observable, of, Subject, throwError } from 'rxjs';
import { jwtDecode } from "jwt-decode";
import { environment } from '../../../../environments/environment';
import { from, fromEvent, timer } from 'rxjs';
import { mergeMap, takeUntil, throttleTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { Logout } from '../_actions/auth.actions';
import { AuthService } from './auth.service';

const API_USER_URL = '/api/user';
const API_URL = environment.API_URL;

@Injectable({
  providedIn: 'root'
})
export class UserService {

  userList: any[];
  userListLoading = false;
  userListChanged = new EventEmitter<any[]>();
  user: User;

  //-------------------------END--------------------------------

  // Check user active/inactive status variables
  //-------------------------START-------------------------------
  private lastActiveStorageKey: string = '_lastActive';
  private userActivityEvents: string[] = ['keydown', 'click', 'wheel', 'mousemove'];
  private recordTimeoutMs: number = 1000 * 15; //every 15 secs
  private timerTickMsForLastActiveState: number = 5 * 1000 * 60; // every 5 mins
  private minutesAllowedInactive: number = 30 * 1000 * 60; // 30 minutes
  private stopTimer$ = new Subject<void>();
  //--------------------------END--------------------------------

  userListRefreshProjection: string = ["_id", "name", "email", "phone", "admin", "plantAdmin", "facility", "unit", "system", "notifications", "active", "graphViewPreference", "docPreferences", "moduleAccess"].join(",");

  private userListSortItem: {id : string, start : "asc" | "desc"};
  constructor(private readonly http: HttpClient, private readonly store: Store<AppState>, private readonly authService: AuthService) { }

  getUserListSortItem () {
    return this.userListSortItem;
  }

  setUserListSortItem (id : string, start : "asc" | "desc") {
    this.userListSortItem = {id, start};
  }

  mapUsersWithDisplayName(users: any[]): any[] {
    return users?.map(user => {
      const name = user.name?.trim();
      const nameParts = name?.split(/\s+/) || [];
      const displayName = nameParts.length >= 2
        ? `${nameParts[0]} ${nameParts[nameParts.length - 1]}`
        : name || 'Unknown';

      return {
        ...user,
        displayName
      };
    });
  }


  getUsersByFilters(filters, fields = null, returnObservable = false) {
    let param: any = filters;
    let url = API_URL + API_USER_URL;
    let httpParams = new HttpParams({ fromObject: param });

    if (fields) {
      httpParams = httpParams.set('fields', fields);
    }

    if(returnObservable)
      return this.http.get(API_URL + API_USER_URL, { params: httpParams, headers: this.getHttpAuthHeader() });

    this.http.get(url, { params: httpParams, headers: this.getHttpAuthHeader() }).subscribe((data: any) => {
      this.userListChanged.emit(this.mapUsersWithDisplayName(data.data));
    });
  }
  searchUsers(q) {
    return this.http.get(API_URL + API_USER_URL + "/search", { params: {q: q}, headers: this.getHttpAuthHeader() })
  }

  getUserDetails(id, fields = null) {
    let param = { fields: 'alertTableColumns' }
    if(fields){
      param.fields = fields;
    }
    return this.http.get(`${API_URL}${API_USER_URL}/${id}`, {params:param , headers: this.getHttpAuthHeader() })
  }

	getUser(fields = null, deactivated = null) {
		let param: any = { 'all': true };
		let url = API_URL + API_USER_URL;

		let httpParams = new HttpParams({ fromObject: param });

		if (deactivated) {
			httpParams = httpParams.set('deactivated', deactivated);
		}


		if (fields) {
			httpParams = httpParams.set('fields', fields);
		}

		this.userListLoading = true;
		this.http.get(url, { params: httpParams, headers: this.getHttpAuthHeader() }).subscribe((data: any) => {
			this.userListLoading = false;
			this.userList = data.data;
			this.userListChanged.emit(this.mapUsersWithDisplayName(data.data));

			// Get logged user and update his details if anything is changed.
			let l_user = JSON.parse(localStorage.getItem('user'));
			let u_user = this.userList.filter(u => u._id === l_user?._id);
			l_user = {...l_user, ...u_user[0]};
			localStorage.setItem('user', JSON.stringify(l_user));
		}, () => {
			this.userListLoading = false;
		});
	}

  getUserFromLocalStorage(){
    return JSON.parse(localStorage.getItem('user'));
  }

  createUserGroup(body) {
    return this.http.post<User>(API_URL + '/api/usergroup', body, { headers: this.getHttpAuthHeader() })
  }

  getUserGroup(fields = null) {
    let url = API_URL + '/api/usergroup';
    let httpParams = new HttpParams();

    if (fields) {
      httpParams = httpParams.set('fields', fields);
    }

    return this.http.get<User>(url, { params: httpParams, headers: this.getHttpAuthHeader() });
  }

  updateUserGroup(body) {
    return this.http.put<User>(API_URL + `/api/usergroup`, body, { headers: this.getHttpAuthHeader() })
  }

  deleteUserGroup(id) {
    return this.http.delete<User>(API_URL + `/api/usergroup?_id=${id}`, { headers: this.getHttpAuthHeader() })
  }

  refreshToken(body) {
    return this.http.post(API_URL + API_USER_URL + `/refresh`, body, { headers: this.getHttpAuthHeader(true), observe: 'response' })
  }

  createUser(u: User, manualSubscription) {

    let user = {
      name: u.name.trim(),
      email: u.email.trim(),
      phone: u.phone?u.phone.trim():'',
      facilityAdminList: u.facilityAdminList || undefined,
      //username: u.username.trim(),
      password: u.password,
      enterprise: u.enterprise || undefined,
      facility: u.facility.length ? u.facility : undefined,
      system: u.system || [],
      unit: u.unit || [],
      modelRetraining: u.modelRetraining || false,
      plantAdmin: u.plantAdmin || false,
      optWhatsapp: u.optWhatsapp || false,
      internal: u.internal,
      moduleAccess: u.moduleAccess || [],
      notifications : u.notifications ? u.notifications : undefined
    }

    let s = this.http.post<UserResponse>(API_URL + API_USER_URL, user, { headers: this.getHttpAuthHeader() })
    if (manualSubscription)
      return s;
    else
      s.subscribe((data: any) => {
        this.getUser(this.userListRefreshProjection);
      });
  }

  createBulkUser(users) {
    let body = {
      users: users,
      csv: true
    }
    return this.http.post(API_URL + API_USER_URL, body, { headers: this.getHttpAuthHeader() })
  }
  updateUser(u: User, manualSubscription) {

    let user = {
      _id: u._id,
      name: u.name.trim(),
      email: u.email.trim(),
      phone: u.phone?u.phone.trim():'',
      facilityAdminList: u.facilityAdminList || undefined,
      notificationSettings: u.notificationSettings || undefined,
      audibleAlarmSettings: u.audibleAlarmSettings || undefined,
      graphViewPreference: u.graphViewPreference || undefined,
      //username: u.username.trim(),
      //password: u.password,
      facility: u.facility || undefined,
      system: u.system || [],
      unit: u.unit || [],
      plantAdmin: u.plantAdmin || false,
      optWhatsapp: u.optWhatsapp || false,
      internal: u.internal || false,
      modelRetraining: u.modelRetraining || false,
      moduleAccess: u.moduleAccess || [],
      notifications : u.notifications ? u.notifications : undefined,
      active: u.active,
      reason: u.reason
    }

    let s = this.http.put<User>(API_URL + API_USER_URL, user, { headers: this.getHttpAuthHeader() })
    if (manualSubscription)
      return s;
    else
      s.subscribe((data: any) => {
        this.getUser(this.userListRefreshProjection);
      });
  }

  updateUserLastActivityTime(data) {
    let url = API_URL + API_USER_URL;

    let body = JSON.stringify({ _id: this.getLoggedInUser()._id, ...data })
    return fetch(`${url}`, { keepalive: true, method: 'PUT', body, headers: { "Content-Type": "application/json",'Authorization': this.getToken()}});
  }

  deleteUser(u: User) {
    let params = new HttpParams().set('_id', u._id);
    if (u.reason) {
      params = params.set('reason', u.reason);
    }
    return this.http.delete<User>(API_URL + API_USER_URL, { params, headers: this.getHttpAuthHeader() });
  }

  getLoggedInUser = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user;
  }

  getHttpAuthHeader(toRefreshToken = false) {
    const userToken = localStorage.getItem(environment.runtime.authTokenKey);
    // Token missing (e.g., logged out from another tab) — dispatch Logout immediately.
    if (!userToken) {
      this.store.dispatch(new Logout());
      return new HttpHeaders();
    }
    let httpHeaders = new HttpHeaders({ Authorization: userToken });
    if(toRefreshToken) {
      const refreshToken = localStorage.getItem(environment.runtime.refreshTokenKey);
      httpHeaders = httpHeaders.append('refresh-token', refreshToken);
    }
    httpHeaders.set("Content-Type", "application/json");
    return httpHeaders;
  }

  getToken() {
    return localStorage.getItem(environment.runtime.authTokenKey);
  }

  public setUpUserActiveStatusCheck() {
    from(this.userActivityEvents)
      .pipe(mergeMap(event => fromEvent(document, event)), throttleTime(this.recordTimeoutMs))
      .subscribe(_ => this.recordLastActiveDate());
  }

  private recordLastActiveDate() {
    const currentDate = new Date();
    localStorage.setItem(this.lastActiveStorageKey, currentDate.toString());
  }

  private getLastActiveFromLocalStorage(): Date | null {
    const valueFromStorage = localStorage.getItem(this.lastActiveStorageKey);
    if (!valueFromStorage) {
      return null;
    }
    return new Date(valueFromStorage);
  }

  getCurrentUser(observableFlag = false, fields = null) {
    let param = new HttpParams();
    const fieldsToUse = fields || this.userListRefreshProjection;
    param = param.set('fields', fieldsToUse);
    let url = API_URL + API_USER_URL;
    if (observableFlag) {
      return this.http.get(url, { params: param, headers: this.getHttpAuthHeader() });
    }
    this.http.get(url, { params: param, headers: this.getHttpAuthHeader() }).subscribe((data: any) => {
      let localUser = this.getLoggedInUser();
      localUser = { ...localUser, ...data};
      localStorage.setItem('user', JSON.stringify(localUser));
    });
  }

  /** Emits whenever graph view preferences are loaded or saved so components (e.g. tag-graph) can react without parent pass-through. */
  private _graphViewPreferences$ = new BehaviorSubject<GraphViewPreference | null>(null);

  /** Default graph-view preference values used when older users have partial/missing preference payloads. */
  private readonly _defaultGraphViewPreferences: GraphViewPreference = {
    anomalyDefault: GraphViewPref.anomalyDefault.collapsed,
    timelineDefault: GraphViewPref.timelineDefault.collapsed,
    graphShading: GraphViewPref.graphShading.shadeByAlert,
    shutdownRegionShading: GraphViewPref.shutdownRegionShading.on,
    tagGraphHeight: GraphViewPref.tagGraphHeight.normal,
    rcaAdvancedView: false,
    casesAdvancedView: true,
    tagLimitsAndValues: true,
    tripLine: false,
    alarmLine: false,
    thresholdLine: false,
    instrumentRangeLine: false,
  };

  private _normalizeGraphViewPreferences(
    preferences?: Partial<GraphViewPreference> | null
  ): GraphViewPreference {
    const normalized = { ...this._defaultGraphViewPreferences, ...(preferences || {}) } as GraphViewPreference & {
      tripLimits?: boolean;
      alarmLimits?: boolean;
      thresholdLimits?: boolean;
      instrumentalRange?: boolean;
    };

    if (normalized.tripLine === null && normalized.tripLimits !== null) {
      normalized.tripLine = !!normalized.tripLimits;
    }
    if (normalized.alarmLine === null && normalized.alarmLimits !== null) {
      normalized.alarmLine = !!normalized.alarmLimits;
    }
    if (normalized.thresholdLine === null && normalized.thresholdLimits !== null) {
      normalized.thresholdLine = !!normalized.thresholdLimits;
    }
    if (normalized.instrumentRangeLine === null && normalized.instrumentalRange !== null) {
      normalized.instrumentRangeLine = !!normalized.instrumentalRange;
    }

    return normalized;
  }

  /** Load graph/view preferences from session (user in localStorage, already fetched when session started). */
  getGraphViewPreferences(): Observable<{ status: number; preferences: GraphViewPreference }> {
    const user = this.getUserFromLocalStorage();
    const preferences = this._normalizeGraphViewPreferences(user?.graphViewPreference);
    return of({ status: 200, preferences }).pipe(
      tap(res => this._graphViewPreferences$.next(res.preferences))
    );
  }

  /** Observable of graph view preferences; emits on load and whenever preferences are saved. Use this in tag-graph so it can react to prefs without parent binding. */
  getGraphViewPreferencesStream(): Observable<GraphViewPreference> {
    const fromFetch = defer(() =>
      this.getGraphViewPreferences().pipe(take(1), map(r => r.preferences), tap(p => this._graphViewPreferences$.next(p)))
    );
    const fromSubject = this._graphViewPreferences$.asObservable().pipe(
      filter((p: GraphViewPreference) => p != null)
    );
    return merge(fromFetch, fromSubject).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay(1)
    );
  }

  /** Save graph/view preferences on current user via PUT /api/user. */
  putGraphViewPreferences(prefs: GraphViewPreference): Observable<{ status: number; preferences: GraphViewPreference }> {
    const user = this.getUserFromLocalStorage();
    if (!user?._id) {
      return throwError(() => new Error('User not found'));
    }
    const preferences = this._normalizeGraphViewPreferences(prefs);
    const previousPreferences = this._graphViewPreferences$.value;
    const updatedUser = { ...user, graphViewPreference: preferences };
    // Optimistic update so graphs (shutdown/shading) react immediately, before the API returns.
    localStorage.setItem('user', JSON.stringify(updatedUser));
    this._graphViewPreferences$.next(preferences);
    return (this.updateUser(updatedUser as User, true) as Observable<any>).pipe(
      map(() => ({ status: 200, preferences })),
      catchError((err) => {
        const rolledBackUser = { ...this.getUserFromLocalStorage(), graphViewPreference: previousPreferences };
        localStorage.setItem('user', JSON.stringify(rolledBackUser));
        if (previousPreferences) {
          this._graphViewPreferences$.next(previousPreferences);
        }
        return throwError(() => err);
      }),
    );
  }

  /** Emits whenever the generic doc-preferences bag is loaded or saved, so components can react without parent pass-through. */
  private _docPreferences$ = new BehaviorSubject<UserPreferences | null>(null);

  /** Load the generic doc-preferences bag from session (user in localStorage, already fetched when session started). */
  getDocPreferences(): Observable<UserPreferences> {
    const user = this.getUserFromLocalStorage();
    const docPreferences = user?.docPreferences || {};
    return of(docPreferences).pipe(
      tap(p => this._docPreferences$.next(p))
    );
  }

  /**
   * Save a single doc-preference key via a dedicated dot-notation PUT (`PUT /api/user/doc-preferences`) —
   * unlike putGraphViewPreferences (which reads, merges, and replaces the *entire* sub-object),
   * this only ever sends the changed key, so concurrent saves of different preference keys from
   * different features/tabs can never clobber each other.
   */
  setDocPreference(key: string, value: any): Observable<UserPreferences> {
    const user = this.getUserFromLocalStorage();
    if (!user?._id) {
      return throwError(() => new Error('User not found'));
    }
    const previousDocPreferences = this._docPreferences$.value || user.docPreferences || {};
    const docPreferences = { ...previousDocPreferences, [key]: value };
    // Optimistic update so dependent UI reacts immediately, before the API returns.
    localStorage.setItem('user', JSON.stringify({ ...user, docPreferences }));
    this._docPreferences$.next(docPreferences);

    return this.http.put<{ status: number; data: UserPreferences }>(
      API_URL + API_USER_URL + '/doc-preferences', { [key]: value }, { headers: this.getHttpAuthHeader() }
    ).pipe(
      map(res => res.data),
      catchError((err) => {
        const rolledBackUser = { ...this.getUserFromLocalStorage(), docPreferences: previousDocPreferences };
        localStorage.setItem('user', JSON.stringify(rolledBackUser));
        this._docPreferences$.next(previousDocPreferences);
        return throwError(() => err);
      }),
    );
  }

  public checkUserInactiveState() {
    timer(0, this.timerTickMsForLastActiveState)
      .pipe(
        takeUntil(this.stopTimer$)
      )
      .subscribe(_ => {
        let lastActiveTime = new Date();
        let currentDateTime = new Date();
        const userToken = localStorage.getItem(environment.runtime.authTokenKey);
        if(!userToken) {
          return;
        }
        const decodedTokenExpiry = jwtDecode(userToken).exp * 1000; // convert to milliseconds
        const decodedTokenIssueAt = jwtDecode(userToken).iat * 1000; // convert to milliseconds
        const validTimeOfJWTInMilliseconds = decodedTokenExpiry - decodedTokenIssueAt;
        const oneDayMilliseconds = 24 * 60 * 60 * 1000;
        if(validTimeOfJWTInMilliseconds > oneDayMilliseconds) {
          this.stopCheckTimerForInactiveUser(); // Call stopTimer method if condition is met
          return;
        }
        if(this.getLastActiveFromLocalStorage()) {
          lastActiveTime = this.getLastActiveFromLocalStorage()
        }
        if ((currentDateTime.getTime() - lastActiveTime.getTime() < this.minutesAllowedInactive) && // User is active in last 30 mins
          (decodedTokenExpiry - currentDateTime.getTime() <= 10 * 1000 * 60) && // Token is going to expire in 10 mins
          (decodedTokenExpiry - currentDateTime.getTime() > 0) // Token should not be already expired
        ){
          const body = {
            _id: this.user._id,
            email: this.user.email
          }
          this.refreshToken(body).subscribe((res: any) => {
            if(res.status === 401 || res.status === 400) {
              this.logoutUser();
            }
            const newAccessToken = res.headers.get('x-auth-token')
            localStorage.setItem(environment.runtime.authTokenKey, newAccessToken);
          })
        }
        else if (currentDateTime.getTime() - lastActiveTime.getTime() >= this.minutesAllowedInactive) {
          this.logoutUser();
        }
      });
  }
  private logoutUser() {
    this.store.dispatch(new Logout());
  }

  stopCheckTimerForInactiveUser() {
    this.stopTimer$.next();
    this.stopTimer$.complete();
  }

}
